/**
 * Org context: current user's organization and role (from GET /api/org/me).
 * Used for Sidebar (org name + role above logout) and Settings Team Members.
 */

import { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { orgAPI } from '../services/api'
import { useAuth } from './AuthContext'

const OrgContext = createContext(null)

export function OrgProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const [org, setOrg] = useState(null)
  const [role, setRole] = useState(null)
  const [allOrgs, setAllOrgs] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchOrgMe = useCallback(async () => {
    if (!isAuthenticated) {
      setOrg(null)
      setRole(null)
      setAllOrgs([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [meRes, listRes] = await Promise.all([
        orgAPI.getMe(),
        orgAPI.listOrgs(),
      ])
      const meData = meRes?.data
      setOrg(meData?.org ?? null)
      setRole(meData?.role ?? null)
      setAllOrgs(listRes?.data?.orgs ?? [])
    } catch (err) {
      // Only clear org state on a true session-invalid 401 (token gone /
      // expired / revoked / missing). A 403 means "you can't see this org's
      // data" — which can happen mid-session for legitimate reasons (e.g.
      // org membership changed, viewing a meeting from a different org) and
      // must NOT wipe the user's primary org context. A bare 401 from a
      // non-session endpoint is also kept because the same defensive guard
      // exists in api.ts. For all other errors (network, 5xx) keep state so
      // transient failures don't blank the dashboard.
      const status = err?.status ?? err?.response?.status
      const code = err?.code ?? err?.body?.code ?? err?.body?.error?.code
      const SESSION_INVALID_CODES = new Set([
        'TOKEN_INVALID',
        'TOKEN_EXPIRED',
        'TOKEN_REVOKED',
        'AUTH_REQUIRED',
        'SESSION_EXPIRED',
      ])
      const isSessionInvalid =
        status === 401 && (
          (code != null && SESSION_INVALID_CODES.has(code)) ||
          /authentication required|session expired|invalid token|token expired/i.test(err?.message ?? '')
        )
      if (isSessionInvalid) {
        setOrg(null)
        setRole(null)
        setAllOrgs([])
      }
      // else: leave existing state intact
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchOrgMe()
  }, [fetchOrgMe])

  const switchOrg = useCallback(async (orgId) => {
    await orgAPI.switchOrg(orgId)
    await fetchOrgMe()
  }, [fetchOrgMe])

  const value = {
    org,
    role,
    allOrgs,
    loading,
    isAdmin: role === 'org_admin',
    isPersonalOrg: org?.isPersonal === true,
    refetchOrg: fetchOrgMe,
    switchOrg,
  }

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  )
}

export function useOrg() {
  const context = useContext(OrgContext)
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return context
}
